/**
 * Hook de Gerenciamento de Progresso
 * 
 * Gerencia o progresso visual do salvamento:
 * - Interval de progresso automático
 * - Controle de velocidade (rápido antes da resposta, parado depois)
 * - Sincronização com responseReceived
 * 
 * Origem: linhas 38, 56-60, 1681-1690 do useSaveController.ts
 */

import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  PROGRESS_INITIAL,
  PROGRESS_INCREMENT,
  PROGRESS_INTERVAL_MS,
  PROGRESS_MAX_WITHOUT_RESPONSE,
  PROGRESS_COMPLETE
} from '../constants/saveConstants';

/**
 * Opções do hook useSaveProgress
 */
export interface UseSaveProgressOptions {
  /**
   * Progresso inicial
   * @default 0
   */
  initialProgress?: number;
  
  /**
   * Incremento por tick
   * @default 5
   */
  increment?: number;
  
  /**
   * Intervalo em ms
   * @default 200
   */
  intervalMs?: number;
  
  /**
   * Progresso máximo sem resposta
   * @default 80
   */
  maxWithoutResponse?: number;
}

/**
 * Retorno do hook useSaveProgress
 */
export interface UseSaveProgressReturn {
  // Estados
  /**
   * Progresso atual (0-100)
   */
  progress: number;
  
  /**
   * Se já recebeu resposta (controla velocidade)
   */
  responseReceived: boolean;
  
  // Actions
  /**
   * Define progresso manualmente
   */
  setProgress: (value: number) => void;
  
  /**
   * Inicia o interval de progresso
   */
  startProgress: () => void;
  
  /**
   * Para o interval de progresso
   */
  stopProgress: () => void;
  
  /**
   * Marca que recebeu resposta (acelera progresso para 100)
   */
  markResponseReceived: () => void;
  
  /**
   * Reseta progresso para inicial
   */
  resetProgress: () => void;
  
  /**
   * Define progresso para completo (100) e para interval
   */
  complete: () => void;
}

/**
 * Hook de gerenciamento de progresso do salvamento
 * 
 * Lógica de progresso:
 * - ANTES da resposta: progresso lento até maxWithoutResponse (padrão: 80%)
 * - DEPOIS da resposta: progresso rápido até 100%
 * - Quando chega em 100%, para o interval automaticamente
 * 
 * @param options - Opções do hook
 * @returns Estados e actions
 * 
 * @example
 * ```typescript
 * const progress = useSaveProgress({ initialProgress: 5 });
 * 
 * // Iniciar progresso
 * progress.startProgress();
 * 
 * // Quando receber resposta do Corel
 * progress.markResponseReceived();
 * 
 * // Completar
 * progress.complete();
 * ```
 */
export function useSaveProgress(options: UseSaveProgressOptions = {}): UseSaveProgressReturn {
  const {
    initialProgress = PROGRESS_INITIAL,
    increment = PROGRESS_INCREMENT,
    intervalMs = PROGRESS_INTERVAL_MS,
    maxWithoutResponse = PROGRESS_MAX_WITHOUT_RESPONSE
  } = options;

  // Estados
  const [progress, setProgressState] = useState<number>(initialProgress);
  const [responseReceived, setResponseReceived] = useState(false);

  // Refs para interval e valores internos
  const progressIntervalRef = useRef<any>(null);
  const progressValueRef = useRef<number>(initialProgress);
  const responseReceivedRef = useRef<boolean>(false);

  /**
   * Define progresso (atualiza state e ref)
   */
  const setProgress = useCallback((value: number) => {
    const clampedValue = Math.max(0, Math.min(100, value));
    progressValueRef.current = clampedValue;
    setProgressState(clampedValue);
  }, []);

  /**
   * Para o interval de progresso
   */
  const stopProgress = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  /**
   * Inicia o interval de progresso
   * 
   * Lógica:
   * - Se já recebeu resposta: incrementa sempre até 100
   * - Se não recebeu: incrementa até maxWithoutResponse, depois para
   */
  const startProgress = useCallback(() => {
    // Parar interval anterior se existir
    stopProgress();

    // Criar novo interval
    progressIntervalRef.current = setInterval(() => {
      setProgressState(prev => {
        const current = progressValueRef.current;

        // Se já recebeu resposta: incrementar sempre até 100
        if (responseReceivedRef.current) {
          const newProgress = current + increment;
          progressValueRef.current = newProgress;

          // Se chegou em 100, parar interval
          if (newProgress >= PROGRESS_COMPLETE) {
            if (progressIntervalRef.current) {
              clearInterval(progressIntervalRef.current);
              progressIntervalRef.current = null;
            }
            return PROGRESS_COMPLETE;
          }

          return newProgress;
        } else {
          // Sem resposta: incrementar até maxWithoutResponse
          const newProgress = current < maxWithoutResponse 
            ? current + increment 
            : maxWithoutResponse;
          progressValueRef.current = newProgress;
          return newProgress;
        }
      });
    }, intervalMs);
  }, [increment, intervalMs, maxWithoutResponse, stopProgress]);

  /**
   * Marca que recebeu resposta
   * 
   * Isso acelera o progresso para completar até 100%
   */
  const markResponseReceived = useCallback(() => {
    responseReceivedRef.current = true;
    setResponseReceived(true);
  }, []);

  /**
   * Reseta progresso para inicial
   */
  const resetProgress = useCallback(() => {
    stopProgress();
    responseReceivedRef.current = false;
    setResponseReceived(false);
    progressValueRef.current = initialProgress;
    setProgressState(initialProgress);
  }, [initialProgress, stopProgress]);

  /**
   * Completa o progresso
   * 
   * Define para 100% e para o interval
   */
  const complete = useCallback(() => {
    stopProgress();
    setProgress(PROGRESS_COMPLETE);
    responseReceivedRef.current = true;
    setResponseReceived(true);
  }, [setProgress, stopProgress]);

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      stopProgress();
    };
  }, [stopProgress]);

  // Memoizar retorno para evitar recriação
  return useMemo(() => ({
    // Estados
    progress,
    responseReceived,

    // Actions
    setProgress,
    startProgress,
    stopProgress,
    markResponseReceived,
    resetProgress,
    complete,
  }), [
    progress,
    responseReceived,
    setProgress,
    startProgress,
    stopProgress,
    markResponseReceived,
    resetProgress,
    complete,
  ]);
}

